import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LogOut, Search, UserCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SIGN_IN_AS_ROLES = ['system_admin', 'executive', 'operations_manager'];

export const SignInAsDialog = ({ open, onOpenChange }) => {
  const { signInAs } = useAuth();
  const [q, setQ] = useState('');
  const [users, setUsers] = useState(null);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      axios.get(`${API}/auth/impersonation/users`, { params: { q } }).then((r) => setUsers(r.data.users)).catch(() => setUsers([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q]);

  const pick = async (u) => {
    setSwitching(u.id);
    try { await signInAs(u.id); } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not sign in as that user');
      setSwitching(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sign-in-as-dialog">
        <DialogHeader>
          <DialogTitle>Sign in as</DialogTitle>
          <DialogDescription>See and use MyHELP exactly as this user would. Everything you do is recorded as done by you on their behalf.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-9" data-testid="sign-in-as-search" />
        </div>
        <div className="max-h-80 overflow-y-auto -mx-1">
          {users === null ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : users.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="sign-in-as-empty">No users found</p>
          ) : users.map((u) => (
            <button key={u.id} type="button" onClick={() => pick(u)} disabled={Boolean(switching)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left hover:bg-muted transition-colors disabled:opacity-60" data-testid={`sign-in-as-user-${u.id}`}>
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{u.name}</div><div className="text-xs text-muted-foreground truncate">{u.email} · {u.role_display}</div></div>
              {switching === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4 text-muted-foreground" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SignedInAsBanner = () => {
  const { user, exitSignInAs } = useAuth();
  const [exiting, setExiting] = useState(false);
  if (!user?.impersonator_id) return null;
  return (
    <div className="relative z-40 flex items-center justify-center gap-3 bg-amber-400 text-amber-950 px-4 py-2 text-sm font-medium" data-testid="signed-in-as-banner">
      <UserCheck className="h-4 w-4 shrink-0" />
      <span>You are signed in as <strong>{user.name}</strong> ({user.impersonator_name} is acting on their behalf)</span>
      <Button size="sm" variant="secondary" className="h-7 bg-amber-950 text-amber-50 hover:bg-amber-900" disabled={exiting}
        onClick={() => { setExiting(true); exitSignInAs(); }} data-testid="signed-in-as-exit-btn">
        {exiting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <LogOut className="h-3.5 w-3.5 mr-1" />}Exit
      </Button>
    </div>
  );
};
